import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api/client';


const buildQuery = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') {
      qs.append(key, val);
    }
  });
  return qs.toString();
};

const MsisdnBlacklistPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ totalItems: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('createdDatetime');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({
    search: '',
    startDate: '',
    endDate: ''
  });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const columns = [
    { id: 'msisdn', label: 'MSISDN' },
    { id: 'createdDatetime', label: 'Created Date Time' },
    { id: 'actions', label: 'Actions', sortable: false }
  ];

  const totalCount = meta?.totalItems ?? items.length;

  const fetchList = async () => {
    setLoading(true);
    setError('');
    try {
      const query = buildQuery({
        page: page + 1,
        pageSize,
        search: filters.search || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        sortBy,
        sortDir
      });
      const data = await apiFetch(`/api/blacklist${query ? `?${query}` : ''}`);
      const list = Array.isArray(data.data) ? data.data : data.data?.items || [];
      setItems(list);
      setMeta(data.meta || { totalItems: list.length });
    } catch (err) {
      setError(err.message || 'Failed to load blacklist');
      setItems([]);
      setMeta({ totalItems: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [page, pageSize, filters, sortBy, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key) => (event) => {
    setFilters((prev) => ({ ...prev, [key]: event.target.value }));
    setPage(0);
  };

  const handleSort = (columnId) => {
    if (columnId === 'actions') return;
    const isAsc = sortBy === columnId && sortDir === 'asc';
    setSortBy(columnId);
    setSortDir(isAsc ? 'desc' : 'asc');
  };

  const handleOpenMenu = (event) => setMenuAnchor(event.currentTarget);
  const handleCloseMenu = () => setMenuAnchor(null);

  const handleDownload = async () => {
    try {
      const query = buildQuery({
        search: filters.search || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        sortBy,
        sortDir
      });
      const resp = await apiFetch(`/api/blacklist/export${query ? `?${query}` : ''}`, {
        parseAs: 'blob'
      });
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'msisdn-blacklist.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download Excel');
    }
  };

  const handleDelete = async (msisdn) => {
    setDeleteError('');
    try {
      await apiFetch('/api/blacklist', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ msisdns: [msisdn] })
      });
      setConfirmDelete(null);
      fetchList();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete');
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  };

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">MSISDN Blacklist</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            MSISDN Blacklist
          </Typography>
          <Typography color="text.secondary">
            Manage blocked phone numbers across the platform.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/msisdn-blacklist/new')}
          >
            + New Blacklist
          </Button>
          <IconButton
            aria-label="blacklist actions"
            onClick={handleOpenMenu}
            sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleCloseMenu}
            PaperProps={{
              sx: {
                background: 'linear-gradient(90deg, #4f46e5 0%, #06b6d4 100%)',
                color: '#fff',
                '& .MuiMenuItem-root': {
                  color: 'inherit',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.08)'
                  }
                }
              }
            }}
          >
            <MenuItem
              onClick={() => {
                handleCloseMenu();
                handleDownload();
              }}
            >
              <DownloadIcon fontSize="small" style={{ marginRight: 8 }} />
              Download Excel
            </MenuItem>
          </Menu>
        </Stack>
      </Box>

      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)'
        }}
      >
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              label="Start Date"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange('startDate')}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', md: 220 } }}
            />
            <TextField
              label="End Date"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange('endDate')}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', md: 220 } }}
            />
            <TextField
              value={filters.search}
              onChange={handleFilterChange('search')}
              placeholder="Filter by MSISDN"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
              sx={{
                flexGrow: 1,
                width: '100%',
                boxSizing: 'border-box',
                minWidth: 0
              }}
            />
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
            border: '1px solid rgba(148, 163, 184, 0.16)',
            borderRadius: 1,
            mt: 3,
            backgroundColor: 'background.paper',
            '&::-webkit-scrollbar': {
              height: 6
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(148, 163, 184, 0.35)',
              borderRadius: 999
            }
          }}
        >
          <TableContainer
            sx={{
              display: 'inline-block',
              minWidth: '100%',
              width: '100%'
            }}
          >
            <Table sx={{ minWidth: 720, borderCollapse: 'separate', borderSpacing: 0 }}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sortDirection={sortBy === column.id ? sortDir : false}
                    >
                      {column.sortable === false ? (
                        column.label
                      ) : (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={sortBy === column.id ? sortDir : 'asc'}
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    hover
                    key={item.msisdn}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(15, 23, 42, 0.02)'
                      }
                    }}
                  >
                    <TableCell sx={{ minWidth: 200 }}>
                      <Typography fontWeight={600}>{item.msisdn}</Typography>
                    </TableCell>
                    <TableCell>{formatDateTime(item.created_datetime || item.createdDatetime)}</TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        aria-label="delete msisdn"
                        onClick={() => setConfirmDelete(item.msisdn)}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!items.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <Box sx={{ textAlign: 'center', py: 5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          No data found
                        </Typography>
                        <Typography color="text.secondary">
                          Adjust filters to see available records.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <TablePagination
          component="div"
          count={totalCount}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[5, 10, 25, 50]}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            const next = parseInt(event.target.value, 10);
            setPageSize(next);
            setPage(0);
          }}
          sx={{
            '& .MuiTablePagination-toolbar': {
              px: { xs: 0, sm: 2 }
            }
          }}
        />
      </Paper>

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
      >
        <DialogTitle>Delete MSISDN</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{confirmDelete}</strong> from the blacklist?
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} color="inherit">
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(confirmDelete)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default MsisdnBlacklistPage;
